import * as React from "react";
import { RiArrowLeftLine, RiArrowRightLine } from "@remixicon/react";
import { IconButton } from "~/components/ui/icon-button";
import { useMediaQuery } from "~/lib/use-media-query";
import { cn } from "~/lib/utils";
import "./carousel.css";

export interface ScrollCarouselHandle {
  readonly scrollEl: HTMLDivElement | null;
}

interface ScrollCarouselProps {
  children: React.ReactNode;
  cardWidth: number;
  cardGap: number;
  ariaLabel?: string;
  navRef?: (el: HTMLDivElement | null) => void;
  prevVariant?: "primary" | "secondary";
  nextVariant?: "primary" | "secondary";
  /** Merged onto the nav block (divider + arrows). */
  navClassName?: string;
  /** Merged onto the divider's background track. */
  trackClassName?: string;
}

export const ScrollCarousel = React.forwardRef<
  ScrollCarouselHandle,
  ScrollCarouselProps
>(function ScrollCarousel(
  {
    children,
    cardWidth,
    cardGap,
    ariaLabel = "Carousel",
    navRef,
    prevVariant = "secondary",
    nextVariant = "secondary",
    navClassName,
    trackClassName,
  },
  ref,
) {
  const isCompact = useMediaQuery("(max-width: 767px)");
  const [canScrollPrev, setCanScrollPrev] = React.useState(false);
  const [canScrollNext, setCanScrollNext] = React.useState(true);
  const [scrollProgress, setScrollProgress] = React.useState(0);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  React.useImperativeHandle(
    ref,
    () => ({
      get scrollEl() {
        return scrollRef.current;
      },
    }),
    [],
  );

  const dragActiveRef = React.useRef(false);
  const dragStartXRef = React.useRef(0);
  const dragStartScrollRef = React.useRef(0);
  const hasDraggedRef = React.useRef(false);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    function update() {
      if (!el) return;
      const maxScroll = el.scrollWidth - el.clientWidth;
      setCanScrollPrev(el.scrollLeft > 0);
      setCanScrollNext(el.scrollLeft < maxScroll - 1);
      setScrollProgress(maxScroll > 0 ? el.scrollLeft / maxScroll : 0);
    }
    update();
    el.addEventListener("scroll", update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", update);
      ro.disconnect();
    };
  }, []);

  function handleDragStart(e: React.PointerEvent<HTMLDivElement>) {
    if (e.pointerType === "touch") return;
    const el = scrollRef.current;
    if (!el) return;
    dragActiveRef.current = true;
    hasDraggedRef.current = false;
    dragStartXRef.current = e.clientX;
    dragStartScrollRef.current = el.scrollLeft;
  }

  function handleDragMove(e: React.PointerEvent<HTMLDivElement>) {
    if (!dragActiveRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    const dx = e.clientX - dragStartXRef.current;
    if (!hasDraggedRef.current && Math.abs(dx) > 4) {
      hasDraggedRef.current = true;
      el.setPointerCapture(e.pointerId);
      el.style.cursor = "grabbing";
    }
    if (hasDraggedRef.current) el.scrollLeft = dragStartScrollRef.current - dx;
  }

  function handleDragEnd(e: React.PointerEvent<HTMLDivElement>) {
    if (e.pointerType === "touch") return;
    dragActiveRef.current = false;
    if (scrollRef.current) scrollRef.current.style.cursor = "";
  }

  function handleClickCapture(e: React.MouseEvent) {
    if (hasDraggedRef.current) {
      e.stopPropagation();
      hasDraggedRef.current = false;
    }
  }

  const step = cardWidth + cardGap;

  return (
    <div className="flex flex-col gap-6">
      <div className="uc-carousel-clip">
        <div
          ref={scrollRef}
          role="region"
          aria-label={ariaLabel}
          tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
          className="flex cursor-grab [scrollbar-width:none] overflow-x-auto pb-1 select-none [&::-webkit-scrollbar]:hidden"
          style={{ gap: cardGap }}
          onPointerDown={handleDragStart}
          onPointerMove={handleDragMove}
          onPointerUp={handleDragEnd}
          onPointerCancel={handleDragEnd}
          onClickCapture={handleClickCapture}
        >
          {children}
          <div className="uc-carousel-right-spacer" />
        </div>
      </div>

      <div
        ref={navRef}
        className={cn("wrap flex flex-col gap-4", navClassName)}
      >
        <div className="relative flex items-center">
          <div
            className={cn("bg-border absolute inset-x-0 h-px", trackClassName)}
          />
          <div
            className="bg-primary relative h-[1px] transition-[width] duration-150 ease-out"
            style={{ width: `${scrollProgress * 100}%` }}
          />
        </div>
        <div
          className={
            isCompact ? "flex justify-end gap-4" : "flex justify-end gap-8"
          }
        >
          <IconButton
            icon={RiArrowLeftLine}
            variant={prevVariant}
            size={isCompact ? "sm" : "default"}
            onClick={() =>
              scrollRef.current?.scrollBy({ left: -step, behavior: "smooth" })
            }
            disabled={!canScrollPrev}
            aria-label="Previous"
          />
          <IconButton
            icon={RiArrowRightLine}
            variant={nextVariant}
            size={isCompact ? "sm" : "default"}
            onClick={() =>
              scrollRef.current?.scrollBy({ left: step, behavior: "smooth" })
            }
            disabled={!canScrollNext}
            aria-label="Next"
          />
        </div>
      </div>
    </div>
  );
});
